import { Task, TaskCategory, TaskPriority, TaskStatus } from "@shared/types";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TasksAPI } from "../services/api";

interface TaskDetailState {
  task: Task | null;
  loading: boolean;
  error: string | null;
}

const TaskDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<TaskDetailState>({
    task: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (id) {
      loadTask(id);
    }
  }, [id]);

  const loadTask = async (taskId: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      console.log("Loading task with ID:", taskId);
      const response = await TasksAPI.getById(taskId);
      console.log("API response:", response);

      if (response.success && response.task) {
        console.log("Task data received:", response.task);
        console.log("Task properties:", {
          id: response.task.id,
          title: response.task.title,
          status: response.task.status,
          priority: response.task.priority,
          category: response.task.category,
          createdAt: response.task.createdAt,
          description: response.task.description,
          notes: response.task.notes,
          estimatedDuration: response.task.estimatedDuration,
        });
        setState((prev) => ({
          ...prev,
          task: response.task!,
          loading: false,
        }));
      } else {
        console.error("API response error:", response.error);
        setState((prev) => ({
          ...prev,
          error: response.error || "Failed to load task",
          loading: false,
        }));
      }
    } catch (error) {
      console.error("Exception while loading task:", error);
      setState((prev) => ({
        ...prev,
        error: "Failed to load task",
        loading: false,
      }));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status: TaskStatus) => {
    if (!status) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          UNKNOWN
        </span>
      );
    }

    const statusColors = {
      [TaskStatus.DRAFT]: "bg-gray-100 text-gray-800",
      [TaskStatus.SUBMITTED]: "bg-blue-100 text-blue-800",
      [TaskStatus.IN_PROGRESS]: "bg-yellow-100 text-yellow-800",
      [TaskStatus.COMPLETED]: "bg-green-100 text-green-800",
      [TaskStatus.CANCELLED]: "bg-red-100 text-red-800",
    };

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusColors[status]}`}
      >
        {status.replace("_", " ").toUpperCase()}
      </span>
    );
  };

  const getPriorityBadge = (priority: TaskPriority) => {
    if (!priority) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          UNKNOWN
        </span>
      );
    }

    const priorityColors = {
      [TaskPriority.LOW]: "bg-gray-100 text-gray-800",
      [TaskPriority.MEDIUM]: "bg-blue-100 text-blue-800",
      [TaskPriority.HIGH]: "bg-orange-100 text-orange-800",
      [TaskPriority.URGENT]: "bg-red-100 text-red-800",
    };

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${priorityColors[priority]}`}
      >
        {priority.toUpperCase()}
      </span>
    );
  };

  const getCategoryDisplay = (category: TaskCategory) => {
    if (!category) {
      return "UNKNOWN";
    }
    return category.replace("_", " ").toUpperCase();
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  if (state.loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading task...</p>
        </div>
      </div>
    );
  }

  if (state.error || !state.task) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">Error</div>
          <p className="text-gray-600 mb-4">
            {state.error || "Task not found"}
          </p>
          <button
            onClick={() => navigate("/tasks")}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
          >
            Back to Tasks
          </button>
        </div>
      </div>
    );
  }

  const task = state.task;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <button
              onClick={() => navigate("/tasks")}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm"
            >
              <svg
                className="w-4 h-4 sm:w-5 sm:h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back to Tasks
            </button>
            <div className="flex flex-wrap gap-2">
              {getStatusBadge(task.status)}
              {getPriorityBadge(task.priority)}
            </div>
          </div>
        </div>

        {/* Task Details */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-4 sm:px-6 border-b border-gray-200">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">
              {task.title}
            </h1>
            <p className="mt-1 text-xs sm:text-sm text-gray-500">
              {getCategoryDisplay(task.category)} • Created{" "}
              {formatDate(task.createdAt)}
            </p>
          </div>

          <div className="px-4 py-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-4">
                  Task Information
                </h3>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-xs sm:text-sm font-medium text-gray-500">
                      Description
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 break-words">
                      {task.description || "No description provided"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs sm:text-sm font-medium text-gray-500">
                      Notes
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 break-words">
                      {task.notes || "No notes provided"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs sm:text-sm font-medium text-gray-500">
                      Estimated Duration
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {task.estimatedDuration
                        ? `${task.estimatedDuration} minutes`
                        : "Not specified"}
                    </dd>
                  </div>
                  {task.submittedAt && (
                    <div>
                      <dt className="text-xs sm:text-sm font-medium text-gray-500">
                        Submitted
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {formatDate(task.submittedAt)}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Location Information */}
              {task.location && (
                <div>
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-4">
                    Location
                  </h3>
                  <dl className="space-y-3">
                    <div>
                      <dt className="text-xs sm:text-sm font-medium text-gray-500">
                        Coordinates
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 font-mono">
                        {task.location.latitude.toFixed(6)},{" "}
                        {task.location.longitude.toFixed(6)}
                      </dd>
                    </div>
                    {task.location.address && (
                      <div>
                        <dt className="text-xs sm:text-sm font-medium text-gray-500">
                          Address
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 break-words">
                          {task.location.address}
                        </dd>
                      </div>
                    )}
                    {task.location.accuracy && (
                      <div>
                        <dt className="text-xs sm:text-sm font-medium text-gray-500">
                          Accuracy
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {task.location.accuracy}m
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Photos Section */}
        {task.photos && task.photos.length > 0 && (
          <div className="mt-6 sm:mt-8 bg-white shadow rounded-lg overflow-hidden">
            <div className="px-4 py-4 sm:px-6 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-medium text-gray-900">
                Photos ({task.photos.length})
              </h2>
            </div>
            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {task.photos.map((photo, index) => (
                  <div key={photo.id} className="relative group">
                    <div className="aspect-w-16 aspect-h-12 bg-gray-200 rounded-lg overflow-hidden">
                      {photo.s3Url ? (
                        <img
                          src={photo.s3Url}
                          alt={`Photo ${index + 1}`}
                          className="w-full h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => window.open(photo.s3Url, "_blank")}
                        />
                      ) : (
                        <div className="flex items-center justify-center h-48 bg-gray-100">
                          <svg
                            className="w-12 h-12 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="mt-2">
                      <p className="text-xs sm:text-sm text-gray-500 truncate">
                        {photo.filename}
                      </p>
                      <p className="text-xs text-gray-400">
                        {(photo.size / 1024 / 1024).toFixed(2)} MB •{" "}
                        {formatDate(photo.capturedAt)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Audio Recordings Section */}
        {task.audioRecordings && task.audioRecordings.length > 0 && (
          <div className="mt-6 sm:mt-8 bg-white shadow rounded-lg overflow-hidden">
            <div className="px-4 py-4 sm:px-6 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-medium text-gray-900">
                Audio Recordings ({task.audioRecordings.length})
              </h2>
            </div>
            <div className="p-4 sm:p-6">
              <div className="space-y-4">
                {task.audioRecordings.map((audio, index) => (
                  <div
                    key={audio.id}
                    className="border border-gray-200 rounded-lg p-3 sm:p-4"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 break-words">
                          {audio.filename}
                        </h4>
                        <p className="text-xs text-gray-500">
                          {(audio.size / 1024 / 1024).toFixed(2)} MB
                          {audio.duration > 0
                            ? ` • ${formatDuration(audio.duration)}`
                            : ""}
                          {" • "}
                          {formatDate(audio.recordedAt)}
                        </p>
                      </div>
                    </div>
                    {audio.s3Url && (
                      <div className="mt-3">
                        <audio controls className="w-full" preload="metadata">
                          <source src={audio.s3Url} type={audio.mimeType} />
                          Your browser does not support the audio element.
                        </audio>
                      </div>
                    )}
                    {!audio.s3Url && (
                      <div className="mt-3 p-3 bg-gray-50 rounded text-center">
                        <p className="text-sm text-gray-500">
                          Audio file not available
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* No Media Message */}
        {(!task.photos || task.photos.length === 0) &&
          (!task.audioRecordings || task.audioRecordings.length === 0) && (
            <div className="mt-6 sm:mt-8 bg-white shadow rounded-lg overflow-hidden">
              <div className="px-4 py-4 sm:px-6 border-b border-gray-200">
                <h2 className="text-base sm:text-lg font-medium text-gray-900">
                  Media
                </h2>
              </div>
              <div className="p-6 text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <p className="mt-2 text-sm text-gray-500">
                  No photos or audio recordings attached to this task.
                </p>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default TaskDetail;
